// SessionStorage utilities for tab persistence - lazy loading: persist active tab on updates, inactive tabs on switch/unmount. Uses optimized sessionStorage with debouncing

import { debouncedSetItem, immediateSetItem, batchSetItems, getItem, removeItem as optimizedRemoveItem } from './optimizedSessionStorage';

// Storage key patterns
const getTabStorageKey = (tabId, dataType) => `tab-${tabId}-${dataType}`;
const ACTIVE_TAB_ID_KEY = 'activeTabId';
const TAB_METADATA_KEY = 'tabMetadata';

// Save individual tab data to sessionStorage (with debouncing for performance)
export const saveTabToSession = (tabId, tabData, immediate = false) => {
  if (!tabId || !tabData) {
    console.warn('saveTabToSession: tabId and tabData are required');
    return false;
  }

  try {
    // Prepare batch write object for efficiency
    const batchWrite = {};
    
    // Save each piece of tab data separately for lazy loading (validationErrors and taricJsonErrors are NOT saved)
    if (tabData.editableData !== undefined) {
      batchWrite[getTabStorageKey(tabId, 'editableData')] = tabData.editableData;
    }

    if (tabData.selectedAgent !== undefined) {
      batchWrite[getTabStorageKey(tabId, 'selectedAgent')] = tabData.selectedAgent;
    }

    if (tabData.selectedSender !== undefined) {
      batchWrite[getTabStorageKey(tabId, 'selectedSender')] = tabData.selectedSender;
    }

    if (tabData.selectedReceiver !== undefined) {
      batchWrite[getTabStorageKey(tabId, 'selectedReceiver')] = tabData.selectedReceiver;
    }

    if (tabData.isDirty !== undefined) {
      batchWrite[getTabStorageKey(tabId, 'isDirty')] = tabData.isDirty;
    }

    if (tabData.status !== undefined) {
      batchWrite[getTabStorageKey(tabId, 'status')] = tabData.status;
    }

    // Use batch write for better performance
    if (immediate) {
      batchSetItems(batchWrite, 0); // Immediate write
    } else {
      batchSetItems(batchWrite, 300); // Debounced write (300ms)
    }

    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn(`Tab ${tabId} data is too large for sessionStorage. Skipping.`, error);
      return false;
    }
    console.error(`Error saving tab ${tabId} to sessionStorage:`, error);
    return false;
  }
};

// Load individual tab data from sessionStorage
export const loadTabFromSession = (tabId) => {
  if (!tabId) {
    return null;
  }

  try {
    const tabData = {};

    // Load editableData
    const editableDataKey = getTabStorageKey(tabId, 'editableData');
    tabData.editableData = getItem(editableDataKey);

    // validationErrors and taricJsonErrors are NOT loaded from sessionStorage (computed from Redux state, not persisted)

    // Load selectedAgent
    const selectedAgentKey = getTabStorageKey(tabId, 'selectedAgent');
    tabData.selectedAgent = getItem(selectedAgentKey);

    // Load selectedSender
    const selectedSenderKey = getTabStorageKey(tabId, 'selectedSender');
    tabData.selectedSender = getItem(selectedSenderKey);

    // Load selectedReceiver
    const selectedReceiverKey = getTabStorageKey(tabId, 'selectedReceiver');
    tabData.selectedReceiver = getItem(selectedReceiverKey);

    // Load isDirty
    const isDirtyKey = getTabStorageKey(tabId, 'isDirty');
    tabData.isDirty = getItem(isDirtyKey, false);

    // Load status
    const statusKey = getTabStorageKey(tabId, 'status');
    tabData.status = getItem(statusKey);

    // Return tabData even if empty - let the caller decide if tab should be created This allows tabs to exist even if they have no data yet
    return tabData;
  } catch (error) {
    console.error(`Error loading tab ${tabId} from sessionStorage:`, error);
    return null;
  }
};

// Save tab metadata (list of tabs and their basic info) Uses immediate write since metadata is critical
export const saveTabMetadata = (tabsMetadata) => {
  try {
    immediateSetItem(TAB_METADATA_KEY, tabsMetadata);
    return true;
  } catch (error) {
    console.error('Error saving tab metadata to sessionStorage:', error);
    return false;
  }
};

// Load tab metadata
export const loadTabMetadata = () => {
  try {
    return getItem(TAB_METADATA_KEY);
  } catch (error) {
    console.error('Error loading tab metadata from sessionStorage:', error);
    return null;
  }
};

// Save active tab ID Uses immediate write since active tab ID is critical
export const saveActiveTabId = (tabId) => {
  try {
    immediateSetItem(ACTIVE_TAB_ID_KEY, tabId);
    return true;
  } catch (error) {
    console.error('Error saving active tab ID to sessionStorage:', error);
    return false;
  }
};

// Load active tab ID
export const loadActiveTabId = () => {
  try {
    return getItem(ACTIVE_TAB_ID_KEY);
  } catch (error) {
    console.error('Error loading active tab ID from sessionStorage:', error);
    return null;
  }
};

// Clear tab data from sessionStorage
export const clearTabFromSession = (tabId) => {
  if (!tabId) {
    return;
  }

  try {
    const dataTypes = ['editableData', 'validationErrors', 'selectedAgent', 'selectedSender', 'selectedReceiver', 'taricJsonErrors', 'isDirty', 'status'];
    
    dataTypes.forEach(dataType => {
      const key = getTabStorageKey(tabId, dataType);
      optimizedRemoveItem(key);
    });
  } catch (error) {
    console.error(`Error clearing tab ${tabId} from sessionStorage:`, error);
  }
};

// Clear all tab data from sessionStorage
export const clearAllTabsFromSession = () => {
  try {
    const keysToRemove = [];
    
    // Find all tab-related keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('tab-') || key === ACTIVE_TAB_ID_KEY || key === TAB_METADATA_KEY)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing all tabs from sessionStorage:', error);
  }
};

// Get all stored tab IDs from sessionStorage
export const getAllStoredTabIds = () => {
  const tabIds = new Set();
  
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('tab-')) {
        // Extract tabId from key pattern: tab-{tabId}-{dataType}
        const match = key.match(/^tab-(.+?)-/);
        if (match && match[1]) {
          tabIds.add(match[1]);
        }
      }
    }
  } catch (error) {
    console.error('Error getting all stored tab IDs from sessionStorage:', error);
  }
  
  return Array.from(tabIds);
};

// Migration utility: Convert array-based state to tab-based structure
export const migrateArrayStateToTabs = (extractedData, selectedAgents = [], selectedSenders = [], selectedReceivers = [], editableData = null) => {
  if (!extractedData) {
    return null;
  }

  const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
  if (declarations.length === 0) {
    return null;
  }

  const tabsMetadata = [];
  
  declarations.forEach((declaration, index) => {
    const tabId = `tab-${Date.now()}-${index}`;
    const editableDataForTab = editableData && Array.isArray(editableData) 
      ? editableData[index] 
      : (editableData && !Array.isArray(editableData) && index === 0 ? editableData : null);

    const tabMetadata = {
      tabId,
      declarationIndex: index,
      label: `Export #${index + 1}`,
      createdAt: Date.now(),
      status: 'draft',
    };

    // Save tab data
    saveTabToSession(tabId, {
      editableData: editableDataForTab || (Array.isArray(declaration) ? null : declaration),
      selectedAgent: selectedAgents[index] || null,
      selectedSender: selectedSenders[index] || null,
      selectedReceiver: selectedReceivers[index] || null,
      validationErrors: null,
      taricJsonErrors: null,
      isDirty: false,
      status: 'draft',
    });

    tabsMetadata.push(tabMetadata);
  });

  // Save metadata
  saveTabMetadata(tabsMetadata);
  
  // Set first tab as active
  if (tabsMetadata.length > 0) {
    saveActiveTabId(tabsMetadata[0].tabId);
  }

  return tabsMetadata;
};



